import pdfMake from 'pdfmake/build/pdfmake.js';
import TurndownService from 'turndown';
import type { JournalEntry } from '@shared/schema';
import type { TDocumentDefinitions, Content, StyleDictionary } from 'pdfmake/interfaces';
import { format } from 'date-fns';

// Helper to ensure pdfMake fonts are loaded
let fontsLoaded = false;
async function ensurePdfMakeFonts(): Promise<void> {
  if (fontsLoaded) return;
  
  try {
    // Set global pdfMake reference before importing fonts
    (globalThis as any).pdfMake = pdfMake;
    
    // Dynamically import vfs_fonts (side-effect import that populates globalThis.pdfMake.vfs)
    await import('pdfmake/build/vfs_fonts.js');
    
    // Copy fonts from global to our pdfMake instance
    if ((globalThis as any).pdfMake?.vfs) {
      (pdfMake as any).vfs = (globalThis as any).pdfMake.vfs;
      fontsLoaded = true;
    } else {
      console.error('pdfMake fonts failed to load: vfs not found on global pdfMake');
    }
  } catch (error) {
    console.error('Failed to load pdfMake fonts:', error);
  }
}

// Enterprise PDF Export Configuration Interface
export interface PDFExportConfig {
  // Basic page setup
  orientation: 'portrait' | 'landscape';
  pageSize: 'letter' | 'a4' | 'legal';
  margins: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  
  // Cover page options
  includeCoverPage: boolean;
  coverPage?: {
    title: string;
    subtitle?: string;
    author?: string;
    showDateRange: boolean;
    showEntryCount: boolean;
    logoUrl?: string;
  };
  
  // Table of contents
  includeTableOfContents: boolean;
  tocOptions?: {
    title: string;
    showPageNumbers: boolean;
    showDates: boolean;
    clickableLinks: boolean;
  };
  
  // Metadata display
  metadataOptions: {
    showDate: boolean;
    showTags: boolean;
    showMood: boolean;
    showWeather: boolean;
    showWordCount: boolean;
    showReadingTime: boolean;
    position: 'top' | 'bottom'; // sidebar not yet implemented
  };
  
  // Layout templates
  layoutTemplate: 'standard' | 'compact' | 'summary' | 'custom';
  customLayout?: {
    includeTitle: boolean;
    includeContent: boolean;
    includeMetadata: boolean;
    contentTruncation?: number; // Max words for summary mode
  };
  
  // Header & footer
  header?: {
    enabled: boolean;
    left?: string;
    center?: string;
    right?: string;
    fontSize?: number;
  };
  footer?: {
    enabled: boolean;
    left?: string;
    center?: string;
    right?: string;
    showPageNumbers: boolean;
    pageNumberFormat?: string; // e.g., "Page {n} of {total}"
    fontSize?: number;
  };
  
  // Styling presets
  stylePreset: 'professional' | 'minimal' | 'creative' | 'academic' | 'custom';
  customStyles?: {
    fontFamily: string;
    fontSize: number;
    titleFontSize: number;
    colorScheme: {
      primary: string;
      secondary: string;
      text: string;
      background: string;
    };
    spacing: {
      paragraphSpacing: number;
      sectionSpacing: number;
    };
  };
  
  // Filter context (for filter-aware exports)
  appliedFilters?: {
    dateRange?: { start: Date; end: Date };
    tags?: string[];
    mood?: string;
    searchQuery?: string;
  };
}

// Page size dimensions in mm
const PAGE_SIZES = {
  letter: { width: 215.9, height: 279.4 },
  a4: { width: 210, height: 297 },
  legal: { width: 215.9, height: 355.6 },
};

// Default PDF Export Configuration
export const getDefaultPDFConfig = (): PDFExportConfig => ({
  orientation: 'portrait',
  pageSize: 'letter',
  margins: { top: 25.4, bottom: 25.4, left: 25.4, right: 25.4 },
  includeCoverPage: false,
  includeTableOfContents: false,
  metadataOptions: {
    showDate: true,
    showTags: true,
    showMood: true,
    showWeather: true,
    showWordCount: false,
    showReadingTime: false,
    position: 'top',
  },
  layoutTemplate: 'standard',
  stylePreset: 'professional',
});

// Helper to extract HTML from various content formats with proper formatting
const getHTMLContent = (content: any): string => {
  if (typeof content === 'string') {
    // Already HTML - return as is
    if (content.startsWith('<')) return content;
    return `<p>${content}</p>`;
  }
  
  if (typeof content === 'object' && content !== null) {
    // If it's already HTML stored as object, convert to string
    if (content.html) return content.html;
    
    // If it's Quill Delta or other format, try to extract text
    if (content.ops) {
      // Comprehensive Quill Delta to HTML conversion preserving all formatting
      let html = '';
      let currentParagraph = '';
      let inList = false;
      let listType = '';
      let listItems: string[] = [];
      
      content.ops.forEach((op: any, index: number) => {
        if (typeof op.insert === 'string') {
          let text = op.insert;
          
          // Apply inline formatting in correct order
          if (op.attributes?.bold) text = `<strong>${text}</strong>`;
          if (op.attributes?.italic) text = `<em>${text}</em>`;
          if (op.attributes?.underline) text = `<u>${text}</u>`;
          if (op.attributes?.strike) text = `<s>${text}</s>`;
          if (op.attributes?.code) text = `<code>${text}</code>`;
          if (op.attributes?.link) text = `<a href="${op.attributes.link}">${text}</a>`;
          
          // Handle line breaks and block-level formatting
          const lines = text.split('\n');
          lines.forEach((line: string, lineIndex: number) => {
            if (lineIndex > 0) {
              // Process previous paragraph/block
              const nextOp = content.ops[index + 1];
              const blockAttrs = nextOp?.attributes || op.attributes || {};
              
              // Check if we're in a list
              if (blockAttrs.list) {
                if (!inList || listType !== blockAttrs.list) {
                  // Close previous list if different type
                  if (inList) {
                    html += listType === 'bullet' ? '<ul>' : '<ol>';
                    listItems.forEach(item => html += `<li>${item}</li>`);
                    html += listType === 'bullet' ? '</ul>' : '</ol>';
                    listItems = [];
                  }
                  inList = true;
                  listType = blockAttrs.list;
                }
                listItems.push(currentParagraph || '<br>');
                currentParagraph = '';
              } else {
                // Close any open list
                if (inList) {
                  html += listType === 'bullet' ? '<ul>' : '<ol>';
                  listItems.forEach(item => html += `<li>${item}</li>`);
                  html += listType === 'bullet' ? '</ul>' : '</ol>';
                  listItems = [];
                  inList = false;
                  listType = '';
                }
                
                // Handle other block types
                if (blockAttrs.header) {
                  const level = blockAttrs.header;
                  html += `<h${level}>${currentParagraph}</h${level}>`;
                } else if (blockAttrs.blockquote) {
                  html += `<blockquote>${currentParagraph}</blockquote>`;
                } else if (blockAttrs['code-block']) {
                  html += `<pre><code>${currentParagraph}</code></pre>`;
                } else {
                  html += currentParagraph ? `<p>${currentParagraph}</p>` : '<p><br></p>';
                }
                currentParagraph = '';
              }
            }
            currentParagraph += line;
          });
        } else if (op.insert?.image) {
          // Handle embedded images
          html += `<img src="${op.insert.image}" style="max-width: 100%; height: auto;" alt="" />`;
        }
      });
      
      // Process final block
      if (inList) {
        html += listType === 'bullet' ? '<ul>' : '<ol>';
        listItems.forEach(item => html += `<li>${item || '<br>'}</li>`);
        html += listType === 'bullet' ? '</ul>' : '</ol>';
      } else if (currentParagraph) {
        html += `<p>${currentParagraph}</p>`;
      }
      
      return html || '<p></p>';
    }
  }
  
  return '<p></p>';
};

// Helper to extract plain text from HTML
const extractPlainText = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

// Helper to calculate word count and reading time
const calculateReadingStats = (html: string): { wordCount: number; readingTime: number } => {
  const text = extractPlainText(html);
  const wordCount = text.trim().split(/\s+/).length;
  const readingTime = Math.ceil(wordCount / 200); // Average 200 words per minute
  return { wordCount, readingTime };
};

// Styling presets for different document types
const STYLE_PRESETS = {
  professional: {
    fontFamily: "'Times New Roman', serif",
    fontSize: 12,
    titleFontSize: 22,
    colorScheme: {
      primary: '#000000',
      secondary: '#555555',
      text: '#000000',
      background: '#ffffff',
    },
    spacing: {
      paragraphSpacing: 12,
      sectionSpacing: 24,
    },
  },
  minimal: {
    fontFamily: "'Helvetica', 'Arial', sans-serif",
    fontSize: 11,
    titleFontSize: 18,
    colorScheme: {
      primary: '#333333',
      secondary: '#666666',
      text: '#333333',
      background: '#ffffff',
    },
    spacing: {
      paragraphSpacing: 8,
      sectionSpacing: 16,
    },
  },
  creative: {
    fontFamily: "'Georgia', serif",
    fontSize: 13,
    titleFontSize: 24,
    colorScheme: {
      primary: '#2c3e50',
      secondary: '#7f8c8d',
      text: '#2c3e50',
      background: '#ecf0f1',
    },
    spacing: {
      paragraphSpacing: 14,
      sectionSpacing: 28,
    },
  },
  academic: {
    fontFamily: "'Garamond', 'Times New Roman', serif",
    fontSize: 12,
    titleFontSize: 20,
    colorScheme: {
      primary: '#1a1a1a',
      secondary: '#4a4a4a',
      text: '#1a1a1a',
      background: '#ffffff',
    },
    spacing: {
      paragraphSpacing: 16,
      sectionSpacing: 32,
    },
  },
} as const;

// Helper to get styles from config
const getStyles = (config: PDFExportConfig) => {
  if (config.stylePreset === 'custom') {
    return config.customStyles || STYLE_PRESETS.professional;
  }
  return STYLE_PRESETS[config.stylePreset as keyof typeof STYLE_PRESETS];
};

// Generate cover page HTML
const generateCoverPage = (config: PDFExportConfig, entries: JournalEntry[]): string => {
  if (!config.includeCoverPage || !config.coverPage) return '';
  
  const styles = getStyles(config);
  
  const dateRange = config.appliedFilters?.dateRange
    ? `${format(config.appliedFilters.dateRange.start, 'MMMM d, yyyy')} - ${format(config.appliedFilters.dateRange.end, 'MMMM d, yyyy')}`
    : `${format(new Date(Math.min(...entries.map(e => new Date(e.createdAt).getTime()))), 'MMMM d, yyyy')} - ${format(new Date(Math.max(...entries.map(e => new Date(e.createdAt).getTime()))), 'MMMM d, yyyy')}`;
  
  return `
    <div style="
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-align: center;
      min-height: 800px;
      font-family: ${styles.fontFamily};
      color: ${styles.colorScheme.text};
      page-break-after: always;
    ">
      ${config.coverPage.logoUrl ? `<img src="${config.coverPage.logoUrl}" style="max-width: 200px; margin-bottom: 40px;" alt="Logo" />` : ''}
      <h1 style="
        font-size: ${styles.titleFontSize + 8}px;
        color: ${styles.colorScheme.primary};
        margin-bottom: 20px;
        font-weight: bold;
      ">${config.coverPage.title}</h1>
      ${config.coverPage.subtitle ? `<h2 style="font-size: ${styles.titleFontSize}px; color: ${styles.colorScheme.secondary}; margin-bottom: 30px;">${config.coverPage.subtitle}</h2>` : ''}
      ${config.coverPage.author ? `<p style="font-size: ${styles.fontSize + 2}px; margin-bottom: 20px; color: ${styles.colorScheme.secondary};">by ${config.coverPage.author}</p>` : ''}
      ${config.coverPage.showDateRange ? `<p style="font-size: ${styles.fontSize}px; color: ${styles.colorScheme.secondary}; margin-top: 40px;">${dateRange}</p>` : ''}
      ${config.coverPage.showEntryCount ? `<p style="font-size: ${styles.fontSize}px; color: ${styles.colorScheme.secondary};">${entries.length} ${entries.length === 1 ? 'Entry' : 'Entries'}</p>` : ''}
    </div>
  `;
};

// Generate table of contents HTML
const generateTableOfContents = (config: PDFExportConfig, entries: JournalEntry[]): string => {
  if (!config.includeTableOfContents || !config.tocOptions) return '';
  
  const styles = getStyles(config);
  const tocOpts = config.tocOptions;
  
  let tocHTML = `
    <div style="
      font-family: ${styles.fontFamily};
      color: ${styles.colorScheme.text};
      page-break-after: always;
      padding-top: 40px;
    ">
      <h1 style="
        font-size: ${styles.titleFontSize}px;
        color: ${styles.colorScheme.primary};
        margin-bottom: 30px;
        font-weight: bold;
        text-align: center;
      ">${tocOpts.title}</h1>
      <div style="margin-top: 40px;">
  `;
  
  entries.forEach((entry, index) => {
    const dateStr = tocOpts.showDates 
      ? format(new Date(entry.createdAt), 'MMMM d, yyyy')
      : '';
    
    tocHTML += `
      <div style="
        display: flex;
        justify-content: space-between;
        margin-bottom: ${styles.spacing.paragraphSpacing}px;
        padding-bottom: ${styles.spacing.paragraphSpacing / 2}px;
        border-bottom: 1px solid #e0e0e0;
      ">
        <div>
          <span style="font-size: ${styles.fontSize}px; color: ${styles.colorScheme.text};">
            ${tocOpts.clickableLinks ? `<a href="#entry-${index}" style="text-decoration: none; color: inherit;">${entry.title}</a>` : entry.title}
          </span>
          ${dateStr ? `<span style="font-size: ${styles.fontSize - 2}px; color: ${styles.colorScheme.secondary}; margin-left: 10px;">${dateStr}</span>` : ''}
        </div>
        ${tocOpts.showPageNumbers ? `<span style="font-size: ${styles.fontSize}px; color: ${styles.colorScheme.secondary};">Page ${index + (config.includeCoverPage ? 2 : 1) + (config.includeTableOfContents ? 1 : 0)}</span>` : ''}
      </div>
    `;
  });
  
  tocHTML += `
      </div>
    </div>
  `;
  
  return tocHTML;
};

// Generate metadata section for an entry
const generateMetadata = (entry: JournalEntry, config: PDFExportConfig, contentHTML: string): string => {
  const styles = getStyles(config);
  
  const { wordCount, readingTime } = calculateReadingStats(contentHTML);
  
  const metadataItems: string[] = [];
  
  if (config.metadataOptions.showDate) {
    metadataItems.push(`<strong>Date:</strong> ${format(new Date(entry.createdAt), 'MMMM d, yyyy')}`);
  }
  
  if (config.metadataOptions.showTags && entry.tags && entry.tags.length > 0) {
    metadataItems.push(`<strong>Tags:</strong> ${entry.tags.join(', ')}`);
  }
  
  if (config.metadataOptions.showMood && entry.mood) {
    metadataItems.push(`<strong>Mood:</strong> ${entry.mood}`);
  }
  
  if (config.metadataOptions.showWeather && entry.weather) {
    metadataItems.push(`<strong>Weather:</strong> ${entry.weather}`);
  }
  
  if (config.metadataOptions.showWordCount) {
    metadataItems.push(`<strong>Words:</strong> ${wordCount.toLocaleString()}`);
  }
  
  if (config.metadataOptions.showReadingTime) {
    metadataItems.push(`<strong>Reading Time:</strong> ${readingTime} min`);
  }
  
  if (metadataItems.length === 0) return '';
  
  return `
    <div style="
      margin-bottom: ${styles.spacing.sectionSpacing}px;
      padding-bottom: ${styles.spacing.paragraphSpacing}px;
      border-bottom: 1px solid #ccc;
    ">
      ${metadataItems.map(item => `<p style="margin: 6px 0; color: ${styles.colorScheme.secondary}; font-size: ${styles.fontSize - 1}px;">${item}</p>`).join('')}
    </div>
  `;
};

// Generate entry content based on layout template
const generateEntryContent = (entry: JournalEntry, config: PDFExportConfig, index: number): string => {
  const styles = getStyles(config);
  
  const contentHTML = getHTMLContent(entry.content);
  
  let entryHTML = index > 0 ? `<div style="page-break-before: always;"></div>` : '';
  
  // Check if using sidebar layout
  const usingSidebar = (config.metadataOptions.position as string) === 'sidebar';
  
  if (usingSidebar) {
    // Sidebar layout: metadata on left, content on right
    entryHTML += `
      <div id="entry-${index}" style="
        display: flex;
        margin-bottom: ${styles.spacing.sectionSpacing}px;
        gap: 20px;
      ">
        <!-- Sidebar metadata -->
        <div style="
          flex: 0 0 180px;
          border-right: 1px solid ${styles.colorScheme.secondary};
          padding-right: 20px;
        ">
          ${generateMetadata(entry, config, contentHTML)}
        </div>
        
        <!-- Main content -->
        <div style="flex: 1;">
          <h2 style="
            color: ${styles.colorScheme.primary};
            margin-top: 0;
            margin-bottom: ${styles.spacing.paragraphSpacing}px;
            font-size: ${styles.titleFontSize}px;
            font-weight: bold;
            word-wrap: break-word;
          ">${entry.title}</h2>
          
          ${generateContentForTemplate(entry, config, contentHTML, styles)}
        </div>
      </div>
    `;
  } else {
    // Standard/Compact/Summary layouts
    entryHTML += `<div id="entry-${index}" style="margin-bottom: ${styles.spacing.sectionSpacing}px;">`;
    
    // Title - check if custom layout allows it
    const showTitle = config.layoutTemplate !== 'custom' || config.customLayout?.includeTitle !== false;
    if (showTitle) {
      entryHTML += `
        <h2 style="
          color: ${styles.colorScheme.primary};
          margin-bottom: ${styles.spacing.paragraphSpacing}px;
          font-size: ${styles.titleFontSize}px;
          font-weight: bold;
          word-wrap: break-word;
        ">${entry.title}</h2>
      `;
    }
    
    // Top metadata - check if custom layout allows it
    const showMetadata = config.layoutTemplate !== 'custom' || config.customLayout?.includeMetadata !== false;
    if (showMetadata && config.metadataOptions.position === 'top') {
      entryHTML += generateMetadata(entry, config, contentHTML);
    }
    
    // Content based on template
    entryHTML += generateContentForTemplate(entry, config, contentHTML, styles);
    
    // Bottom metadata - check if custom layout allows it
    if (showMetadata && config.metadataOptions.position === 'bottom') {
      const metadata = generateMetadata(entry, config, contentHTML);
      if (metadata) {
        entryHTML += `<div style="margin-top: ${styles.spacing.sectionSpacing}px;">${metadata}</div>`;
      }
    }
    
    entryHTML += `</div>`;
  }
  
  return entryHTML;
};

// ===============================================
// PDFMAKE-BASED ENTERPRISE PDF EXPORT FUNCTIONS
// ===============================================

// Convert HTML content to pdfMake content with rich text formatting
const htmlToPdfMakeContent = (html: string): Content[] => {
  const content: Content[] = [];
  
  // Create a temporary DOM to parse HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  // Helper to convert DOM node to pdfMake content
  const convertNode = (node: Node): any => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim();
      return text ? { text } : null;
    }
    
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      const tagName = element.tagName.toLowerCase();
      const children = Array.from(element.childNodes).map(convertNode).filter(Boolean);
      
      // Headings
      if (tagName.match(/^h[1-6]$/)) {
        const level = parseInt(tagName[1]);
        return {
          text: element.textContent || '',
          style: 'heading' + level,
          margin: [0, level === 1 ? 16 : 12, 0, 8] as [number, number, number, number],
        };
      }
      
      // Paragraphs
      if (tagName === 'p') {
        if (children.length === 0 && !element.textContent?.trim()) return null;
        return {
          text: children.length > 0 ? children : element.textContent || '',
          margin: [0, 0, 0, 8] as [number, number, number, number],
        };
      }
      
      // Lists
      if (tagName === 'ul' || tagName === 'ol') {
        const items = Array.from(element.querySelectorAll(':scope > li')).map(li => {
          const liChildren = Array.from(li.childNodes).map(convertNode).filter(Boolean);
          return liChildren.length > 0 ? liChildren : li.textContent || '';
        });
        
        return tagName === 'ul' 
          ? { ul: items, margin: [0, 0, 0, 8] as [number, number, number, number] }
          : { ol: items, margin: [0, 0, 0, 8] as [number, number, number, number] };
      }
      
      // Blockquote
      if (tagName === 'blockquote') {
        return {
          text: element.textContent || '',
          style: 'quote',
          margin: [20, 8, 0, 8] as [number, number, number, number],
        };
      }
      
      // Code blocks
      if (tagName === 'pre' || tagName === 'code') {
        return {
          text: element.textContent || '',
          style: 'code',
          margin: [0, 4, 0, 4] as [number, number, number, number],
        };
      }
      
      // Line break
      if (tagName === 'br') {
        return { text: '\n' };
      }
      
      // Formatting (bold, italic, underline, strikethrough)
      if (tagName === 'strong' || tagName === 'b') {
        return { text: element.textContent || '', bold: true };
      }
      if (tagName === 'em' || tagName === 'i') {
        return { text: element.textContent || '', italics: true };
      }
      if (tagName === 'u') {
        return { text: element.textContent || '', decoration: 'underline' };
      }
      if (tagName === 's' || tagName === 'strike' || tagName === 'del') {
        return { text: element.textContent || '', decoration: 'lineThrough' };
      }
      
      // Divs and spans - process children
      if (tagName === 'div' || tagName === 'span') {
        return children.length > 0 ? children : null;
      }
      
      // Default: extract text content
      return element.textContent?.trim() ? { text: element.textContent } : null;
    }
    
    return null;
  };
  
  // Process body content
  Array.from(doc.body.childNodes).forEach(node => {
    const converted = convertNode(node);
    if (converted) {
      if (Array.isArray(converted)) {
        content.push(...converted);
      } else {
        content.push(converted);
      }
    }
  });
  
  // Fallback to plain text if no content
  if (content.length === 0) {
    const text = extractPlainText(html);
    const paragraphs = text.split('\n').filter(p => p.trim());
    return paragraphs.map(p => ({
      text: p,
      margin: [0, 0, 0, 8] as [number, number, number, number],
    }));
  }
  
  return content;
};

// Generate pdfMake document definition for journal export
const generatePdfMakeDocument = (entries: JournalEntry[], config: PDFExportConfig): TDocumentDefinitions => {
  const styles = getStyles(config);
  const content: Content[] = [];
  
  // Add cover page if enabled
  if (config.includeCoverPage && config.coverPage) {
    const dateRange = entries.length > 0
      ? `${format(new Date(Math.min(...entries.map(e => new Date(e.createdAt).getTime()))), 'MMMM d, yyyy')} - ${format(new Date(Math.max(...entries.map(e => new Date(e.createdAt).getTime()))), 'MMMM d, yyyy')}`
      : format(new Date(), 'MMMM d, yyyy');
    
    content.push(
      { text: config.coverPage.title, style: 'coverTitle', alignment: 'center' as const, margin: [0, 100, 0, 20] as [number, number, number, number] },
      ...(config.coverPage.subtitle ? [{ text: config.coverPage.subtitle, style: 'coverSubtitle', alignment: 'center' as const, margin: [0, 0, 0, 30] as [number, number, number, number] }] : []),
      ...(config.coverPage.author ? [{ text: `by ${config.coverPage.author}`, style: 'coverAuthor', alignment: 'center' as const, margin: [0, 0, 0, 20] as [number, number, number, number] }] : []),
      ...(config.coverPage.showDateRange ? [{ text: dateRange, style: 'coverDate', alignment: 'center' as const, margin: [0, 40, 0, 10] as [number, number, number, number] }] : []),
      ...(config.coverPage.showEntryCount ? [{ text: `${entries.length} ${entries.length === 1 ? 'Entry' : 'Entries'}`, style: 'coverDate', alignment: 'center' as const }] : []),
      { text: '', pageBreak: 'after' as const }
    );
  }
  
  // Add table of contents if enabled
  if (config.includeTableOfContents && config.tocOptions) {
    content.push(
      { text: config.tocOptions.title, style: 'tocTitle', margin: [0, 0, 0, 20] as [number, number, number, number] },
      { 
        ul: entries.map((entry, index) => {
          const dateStr = config.tocOptions!.showDates 
            ? ` (${format(new Date(entry.createdAt), 'MMM d, yyyy')})`
            : '';
          return {
            text: `${entry.title}${dateStr}`,
            ...(config.tocOptions!.clickableLinks ? { linkToPage: index + 1 } : {})
          };
        }),
        margin: [0, 0, 0, 20] as [number, number, number, number]
      },
      { text: '', pageBreak: 'after' }
    );
  }
  
  // Add filter banner if filters applied
  if (config.appliedFilters) {
    const filters: string[] = [];
    if (config.appliedFilters.dateRange) {
      filters.push(`Date Range: ${format(config.appliedFilters.dateRange.start, 'MMM d, yyyy')} - ${format(config.appliedFilters.dateRange.end, 'MMM d, yyyy')}`);
    }
    if (config.appliedFilters.tags && config.appliedFilters.tags.length > 0) {
      filters.push(`Tags: ${config.appliedFilters.tags.join(', ')}`);
    }
    if (config.appliedFilters.mood) {
      filters.push(`Mood: ${config.appliedFilters.mood}`);
    }
    if (config.appliedFilters.searchQuery) {
      filters.push(`Search: "${config.appliedFilters.searchQuery}"`);
    }
    
    if (filters.length > 0) {
      content.push({
        text: `Applied Filters: ${filters.join(' | ')}`,
        style: 'filterBanner',
        margin: [0, 0, 0, 20] as [number, number, number, number]
      });
    }
  }
  
  // Add entries
  entries.forEach((entry, index) => {
    const contentHTML = getHTMLContent(entry.content);
    const { wordCount, readingTime } = calculateReadingStats(contentHTML);
    
    // Page break before each entry except first
    if (index > 0) {
      content.push({ text: '', pageBreak: 'before' });
    }
    
    // Title (if not hidden by custom layout)
    const showTitle = config.layoutTemplate !== 'custom' || config.customLayout?.includeTitle !== false;
    if (showTitle) {
      content.push({
        text: entry.title,
        style: 'entryTitle',
        margin: [0, 0, 0, 12] as [number, number, number, number]
      });
    }
    
    // Metadata
    const showMetadata = config.layoutTemplate !== 'custom' || config.customLayout?.includeMetadata !== false;
    if (showMetadata && config.metadataOptions.position === 'top') {
      const metadataItems: string[] = [];
      if (config.metadataOptions.showDate) {
        metadataItems.push(`Date: ${format(new Date(entry.createdAt), 'MMMM d, yyyy')}`);
      }
      if (config.metadataOptions.showTags && entry.tags && entry.tags.length > 0) {
        metadataItems.push(`Tags: ${entry.tags.join(', ')}`);
      }
      if (config.metadataOptions.showMood && entry.mood) {
        metadataItems.push(`Mood: ${entry.mood}`);
      }
      if (config.metadataOptions.showWeather && entry.weather) {
        metadataItems.push(`Weather: ${entry.weather}`);
      }
      if (config.metadataOptions.showWordCount) {
        metadataItems.push(`Words: ${wordCount.toLocaleString()}`);
      }
      if (config.metadataOptions.showReadingTime) {
        metadataItems.push(`Reading Time: ${readingTime} min`);
      }
      
      if (metadataItems.length > 0) {
        content.push({
          text: metadataItems.join(' | '),
          style: 'metadata',
          margin: [0, 0, 0, 12] as [number, number, number, number]
        });
      }
    }
    
    // Content based on layout template
    if (config.layoutTemplate === 'summary') {
      // Summary: title + metadata only, no content by default
      if (config.customLayout?.includeContent === true && config.customLayout?.contentTruncation) {
        const words = extractPlainText(contentHTML).split(/\s+/).slice(0, config.customLayout.contentTruncation);
        content.push({
          text: words.join(' ') + '...',
          style: 'summaryExcerpt',
          margin: [0, 0, 0, 12] as [number, number, number, number]
        });
      }
    } else if (config.layoutTemplate === 'compact') {
      // Compact: full content with smaller spacing
      const compactContent = htmlToPdfMakeContent(contentHTML);
      compactContent.forEach(item => {
        content.push({ ...(item as unknown as Record<string, unknown>), style: 'compactContent' } as Content);
      });
    } else if (config.layoutTemplate === 'standard' || config.layoutTemplate === 'custom') {
      // Standard: full content
      if (config.layoutTemplate !== 'custom' || config.customLayout?.includeContent !== false) {
        content.push(...htmlToPdfMakeContent(contentHTML));
      }
    }
    
    // Bottom metadata
    if (showMetadata && config.metadataOptions.position === 'bottom') {
      const metadataItems: string[] = [];
      if (config.metadataOptions.showDate) {
        metadataItems.push(`Date: ${format(new Date(entry.createdAt), 'MMMM d, yyyy')}`);
      }
      if (config.metadataOptions.showTags && entry.tags && entry.tags.length > 0) {
        metadataItems.push(`Tags: ${entry.tags.join(', ')}`);
      }
      if (config.metadataOptions.showMood && entry.mood) {
        metadataItems.push(`Mood: ${entry.mood}`);
      }
      if (config.metadataOptions.showWeather && entry.weather) {
        metadataItems.push(`Weather: ${entry.weather}`);
      }
      if (config.metadataOptions.showWordCount) {
        metadataItems.push(`Words: ${wordCount.toLocaleString()}`);
      }
      if (config.metadataOptions.showReadingTime) {
        metadataItems.push(`Reading Time: ${readingTime} min`);
      }
      
      if (metadataItems.length > 0) {
        content.push({
          text: metadataItems.join(' | '),
          style: 'metadata',
          margin: [0, 12, 0, 0] as [number, number, number, number]
        });
      }
    }
  });
  
  // Build style dictionary
  const styleDict: StyleDictionary = {
    coverTitle: {
      fontSize: styles.titleFontSize + 8,
      bold: true,
      color: styles.colorScheme.primary,
    },
    coverSubtitle: {
      fontSize: styles.titleFontSize,
      color: styles.colorScheme.secondary,
    },
    coverAuthor: {
      fontSize: styles.fontSize + 2,
      color: styles.colorScheme.secondary,
    },
    coverDate: {
      fontSize: styles.fontSize,
      color: styles.colorScheme.secondary,
    },
    tocTitle: {
      fontSize: styles.titleFontSize,
      bold: true,
      color: styles.colorScheme.primary,
    },
    filterBanner: {
      fontSize: styles.fontSize - 1,
      color: styles.colorScheme.secondary,
      italics: true,
    },
    entryTitle: {
      fontSize: styles.titleFontSize,
      bold: true,
      color: styles.colorScheme.primary,
    },
    metadata: {
      fontSize: styles.fontSize - 1,
      color: styles.colorScheme.secondary,
    },
    summaryExcerpt: {
      fontSize: styles.fontSize,
      color: styles.colorScheme.secondary,
      italics: true,
    },
    compactContent: {
      fontSize: styles.fontSize - 1,
      lineHeight: 1.4,
    },
    // Rich text content styles
    heading1: {
      fontSize: styles.titleFontSize + 4,
      bold: true,
      color: styles.colorScheme.primary,
    },
    heading2: {
      fontSize: styles.titleFontSize,
      bold: true,
      color: styles.colorScheme.primary,
    },
    heading3: {
      fontSize: styles.fontSize + 4,
      bold: true,
      color: styles.colorScheme.primary,
    },
    heading4: {
      fontSize: styles.fontSize + 2,
      bold: true,
      color: styles.colorScheme.primary,
    },
    heading5: {
      fontSize: styles.fontSize + 1,
      bold: true,
      color: styles.colorScheme.primary,
    },
    heading6: {
      fontSize: styles.fontSize,
      bold: true,
      color: styles.colorScheme.primary,
    },
    quote: {
      italics: true,
      color: styles.colorScheme.secondary,
    },
    code: {
      font: 'Courier',
      fontSize: styles.fontSize - 1,
      background: '#f5f5f5',
    },
  };
  
  // Build document definition
  const docDefinition: TDocumentDefinitions = {
    content,
    styles: styleDict,
    pageSize: config.pageSize.toUpperCase() as TDocumentDefinitions['pageSize'],
    pageOrientation: config.orientation,
    pageMargins: [config.margins.left, config.margins.top, config.margins.right, config.margins.bottom] as [number, number, number, number],
    
    // Header function (on every page)
    header: config.header?.enabled ? (currentPage, pageCount) => {
      return {
        margin: [config.margins.left, config.margins.top / 2, config.margins.right, 0] as [number, number, number, number],
        columns: [
          { text: config.header?.left || '', style: 'headerFooter', alignment: 'left', width: '33%' },
          { text: config.header?.center || '', style: 'headerFooter', alignment: 'center', width: '34%' },
          { text: config.header?.right || '', style: 'headerFooter', alignment: 'right', width: '33%' },
        ],
      };
    } : undefined,
    
    // Footer function (on every page)
    footer: config.footer?.enabled ? (currentPage, pageCount) => {
      const pageNumberText = config.footer?.showPageNumbers
        ? (config.footer.pageNumberFormat || 'Page {n} of {total}')
            .replace('{n}', currentPage.toString())
            .replace('{total}', pageCount.toString())
        : '';
      
      return {
        margin: [config.margins.left, 0, config.margins.right, config.margins.bottom / 2] as [number, number, number, number],
        columns: [
          { text: config.footer?.left || '', style: 'headerFooter', alignment: 'left', width: '33%' },
          { text: config.footer?.center || pageNumberText, style: 'headerFooter', alignment: 'center', width: '34%' },
          { text: config.footer?.right || '', style: 'headerFooter', alignment: 'right', width: '33%' },
        ],
      };
    } : undefined,
  };
  
  // Add header/footer style
  if (config.header?.enabled || config.footer?.enabled) {
    docDefinition.styles!.headerFooter = {
      fontSize: 9,
      color: styles.colorScheme.secondary,
    };
  }
  
  return docDefinition;
};

// Helper to generate content based on layout template
const generateContentForTemplate = (entry: JournalEntry, config: PDFExportConfig, contentHTML: string, styles: any): string => {
  const template = config.layoutTemplate;
  
  // Summary mode: title + metadata only, no content by default
  if (template === 'summary') {
    // Only show excerpt if explicitly enabled via custom layout
    if (config.customLayout?.includeContent === true && config.customLayout?.contentTruncation) {
      const truncateWords = config.customLayout.contentTruncation;
      const words = extractPlainText(contentHTML).split(/\s+/).slice(0, truncateWords);
      return `
        <div style="
          font-size: ${styles.fontSize}px;
          color: ${styles.colorScheme.secondary};
          font-style: italic;
          margin-top: ${styles.spacing.paragraphSpacing}px;
        ">
          <p>${words.join(' ')}${words.length >= truncateWords ? '...' : ''}</p>
        </div>
      `;
    }
    return ''; // Summary mode shows NO content by default
  }
  
  // Compact mode: condensed content with smaller spacing
  if (template === 'compact') {
    return `
      <div style="
        font-size: ${styles.fontSize - 1}px;
        word-wrap: break-word;
        overflow-wrap: break-word;
        line-height: 1.4;
      ">
        ${contentHTML}
      </div>
    `;
  }
  
  // Standard mode: full content
  if (template === 'standard') {
    return `
      <div style="
        font-size: ${styles.fontSize}px;
        word-wrap: break-word;
        overflow-wrap: break-word;
      ">
        ${contentHTML}
      </div>
    `;
  }
  
  // Custom mode: respect custom layout settings
  if (template === 'custom' && config.customLayout) {
    if (config.customLayout.includeContent === false) {
      return '';
    }
    
    let processedContent = contentHTML;
    if (config.customLayout.contentTruncation) {
      const words = extractPlainText(contentHTML).split(/\s+/).slice(0, config.customLayout.contentTruncation);
      processedContent = `<p>${words.join(' ')}...</p>`;
    }
    
    return `
      <div style="
        font-size: ${styles.fontSize}px;
        word-wrap: break-word;
        overflow-wrap: break-word;
      ">
        ${processedContent}
      </div>
    `;
  }
  
  // Default: show full content
  return `
    <div style="
      font-size: ${styles.fontSize}px;
      word-wrap: break-word;
      overflow-wrap: break-word;
    ">
      ${contentHTML}
    </div>
  `;
};

// Export single entry to PDF with enterprise configuration
export const exportToPDF = async (entry: JournalEntry, config?: PDFExportConfig): Promise<void> => {
  // Use default config if none provided
  const pdfConfig: PDFExportConfig = config || getDefaultPDFConfig();
  
  // Export as single-entry array
  await exportMultipleEntries([entry], 'pdf', pdfConfig);
};

// Export single entry to Markdown
export const exportToMarkdown = (entry: JournalEntry): void => {
  const turndownService = new TurndownService({
    headingStyle: 'atx',
    codeBlockStyle: 'fenced',
    bulletListMarker: '-',
  });

  // Custom rules for better markdown conversion
  turndownService.addRule('strikethrough', {
    filter: ['s', 'strike', 'del'] as any,
    replacement: (content: string) => `~~${content}~~`
  });

  turndownService.addRule('underline', {
    filter: ['u'] as any,
    replacement: (content: string) => `__${content}__`
  });

  const htmlContent = getHTMLContent(entry.content);
  const markdownContent = turndownService.turndown(htmlContent);

  // Build markdown with metadata header
  let markdown = `# ${entry.title}\n\n`;
  
  const dateStr = format(new Date(entry.createdAt), 'MMMM d, yyyy');
  markdown += `**Date:** ${dateStr}\n\n`;

  if (entry.tags && entry.tags.length > 0) {
    markdown += `**Tags:** ${entry.tags.join(', ')}\n\n`;
  }

  if (entry.mood) {
    markdown += `**Mood:** ${entry.mood}\n\n`;
  }

  if (entry.weather) {
    markdown += `**Weather:** ${entry.weather}\n\n`;
  }

  markdown += '---\n\n';
  markdown += markdownContent;

  // Create blob and download
  const filename = `${entry.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${format(new Date(entry.createdAt), 'yyyy-MM-dd')}.md`;
  downloadFile(new Blob([markdown], { type: 'text/markdown' }), filename);
};

// Export single entry to JSON
export const exportToJSON = (entry: JournalEntry): void => {
  const jsonData = {
    id: entry.id,
    title: entry.title,
    content: entry.content,
    plainContent: entry.plainContent,
    tags: entry.tags,
    mood: entry.mood,
    weather: entry.weather,
    isPrivate: entry.isPrivate,
    aiInsights: entry.aiInsights,
    isPinned: entry.isPinned,
    createdAt: entry.createdAt,
    updatedAt: entry.updatedAt,
  };

  const jsonString = JSON.stringify(jsonData, null, 2);
  const filename = `${entry.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${format(new Date(entry.createdAt), 'yyyy-MM-dd')}.json`;
  
  downloadFile(new Blob([jsonString], { type: 'application/json' }), filename);
};

// Export multiple entries in chosen format
export const exportMultipleEntries = async (
  entries: JournalEntry[], 
  exportFormat: 'pdf' | 'markdown' | 'json',
  pdfConfig?: PDFExportConfig
): Promise<void> => {
  if (entries.length === 0) return;

  if (exportFormat === 'json') {
    // Export all entries as single JSON file
    const jsonData = entries.map(entry => ({
      id: entry.id,
      title: entry.title,
      content: entry.content,
      plainContent: entry.plainContent,
      tags: entry.tags,
      mood: entry.mood,
      weather: entry.weather,
      isPrivate: entry.isPrivate,
      aiInsights: entry.aiInsights,
      isPinned: entry.isPinned,
      createdAt: entry.createdAt,
      updatedAt: entry.updatedAt,
    }));

    const jsonString = JSON.stringify(jsonData, null, 2);
    const filename = `journal-entries-${format(new Date(), 'yyyy-MM-dd')}.json`;
    downloadFile(new Blob([jsonString], { type: 'application/json' }), filename);
  } else if (exportFormat === 'markdown') {
    // Combine all entries into one markdown file
    const turndownService = new TurndownService({
      headingStyle: 'atx',
      codeBlockStyle: 'fenced',
      bulletListMarker: '-',
    });

    turndownService.addRule('strikethrough', {
      filter: ['s', 'strike', 'del'] as any,
      replacement: (content: string) => `~~${content}~~`
    });

    turndownService.addRule('underline', {
      filter: ['u'] as any,
      replacement: (content: string) => `__${content}__`
    });

    let combinedMarkdown = `# Journal Entries Export\n\n`;
    combinedMarkdown += `*Exported on ${format(new Date(), 'MMMM d, yyyy')}*\n\n`;
    combinedMarkdown += `---\n\n`;

    entries.forEach((entry, index) => {
      const htmlContent = getHTMLContent(entry.content);
      const markdownContent = turndownService.turndown(htmlContent);

      combinedMarkdown += `## ${entry.title}\n\n`;
      
      const dateStr = format(new Date(entry.createdAt), 'MMMM d, yyyy');
      combinedMarkdown += `**Date:** ${dateStr}\n\n`;

      if (entry.tags && entry.tags.length > 0) {
        combinedMarkdown += `**Tags:** ${entry.tags.join(', ')}\n\n`;
      }

      if (entry.mood) {
        combinedMarkdown += `**Mood:** ${entry.mood}\n\n`;
      }

      if (entry.weather) {
        combinedMarkdown += `**Weather:** ${entry.weather}\n\n`;
      }

      combinedMarkdown += markdownContent;
      
      if (index < entries.length - 1) {
        combinedMarkdown += `\n\n---\n\n`;
      }
    });

    const filename = `journal-entries-${format(new Date(), 'yyyy-MM-dd')}.md`;
    downloadFile(new Blob([combinedMarkdown], { type: 'text/markdown' }), filename);
  } else if (exportFormat === 'pdf') {
    // Use default config if none provided
    const config: PDFExportConfig = pdfConfig || getDefaultPDFConfig();
    
    // Ensure fonts are loaded before generating PDF
    await ensurePdfMakeFonts();
    
    // Generate pdfMake document definition
    const docDefinition = generatePdfMakeDocument(entries, config);
    
    // Create and download PDF
    const filename = `journal-entries-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
    
    // Use pdfMake to create and download the PDF
    pdfMake.createPdf(docDefinition).download(filename);
  }
};

// Helper function to trigger file download
export const downloadFile = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
